import React, { useEffect, useState, useRef } from "react";
import styled from "styled-components";
import { toggle24hr } from "funcs/timeFormat24hr";
import useToggleDropdown from "hooks/useToggleDropdown";
import { BsCalendar2Plus } from "react-icons/bs";
import { SiGooglecalendar } from "react-icons/si";
import { RiAppleLine } from "react-icons/ri";
import { PiMicrosoftOutlookLogoThin } from "react-icons/pi";
import { BiMailSend } from "react-icons/bi";
import SpinnerSmall from "components/SpinnerSmall";
import { Tooltip } from "@mui/material";
import { LuMailCheck } from "react-icons/lu";
import { useSelector, useDispatch } from "react-redux";
import { rsvpEvent } from "features/landingPageSlice";
import { useTranslation } from "react-i18next";
import { useAppLanguage } from "i18n/useAppLanguage";

export default function EventDetailsExample() {
  const { t } = useTranslation("events");
  const { currentLocale } = useAppLanguage();

  const calMenuRef = useRef(null);
  const [coordinates, setCoordinates] = useState(null);
  const [isDropped, setIsDropped] = useToggleDropdown(calMenuRef);
  const handleDropDown = () => {
    setIsDropped.toggle(calMenuRef);
  };

  const dispatch = useDispatch();
  const { eventRsvped, eventRsvpLoading } = useSelector(
    (state) => state.landingPage,
  );

  useEffect(() => {
    setCoordinates(["-82.40001579999999", "34.8484984"]);
  }, []);

  return (
    <DetailsContainer>
      <EventDetailsTop>
        <EventDetailsTopAttending>
          55
          {t("actions.going")}
        </EventDetailsTopAttending>
        <MyEventStatus>
          {eventRsvped ? (
            <Tooltip title={t("rsvp.attendingHelp")} placement="top">
              <RSVPBtn spinIcon={"none"} bg={"green"}>
                <LuMailCheck style={{ marginRight: "10px" }} />
                {t("actions.going")}
              </RSVPBtn>
            </Tooltip>
          ) : (
            <RSVPBtn
              spinIcon={"spin 0.5s"}
              disabled={eventRsvpLoading}
              bg={"var(--themeColor)"}
              onClick={() => dispatch(rsvpEvent("attending"))}
            >
              {eventRsvpLoading ? (
                <SpinnerSmall />
              ) : (
                <>
                  <BiMailSend style={{ marginRight: "10px" }} />
                  RSVP
                </>
              )}
            </RSVPBtn>
          )}
        </MyEventStatus>
      </EventDetailsTop>
      <EventDescription>
        <EventDescriptionTitle>{t("detail.about")}</EventDescriptionTitle>
        <EventDescriptionText hasMap={coordinates ? true : false}>
          <TimeAndDate>
            {
              <span style={{ marginBottom: "5px" }}>
                <b style={{ marginRight: "5px" }}>Starts:</b>
                {new Intl.DateTimeFormat(currentLocale, {
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                }).format(new Date("2025-10-06T04:00:00.000+00:00"))}

                <span>
                  <b> at </b> {toggle24hr("15:30", false)}
                </span>
              </span>
            }
            <AddToCalendarMenu ref={calMenuRef}>
              <AddToCalendarButton onClick={handleDropDown}>
                <BsCalendar2Plus size={22} />
                {t("actions.addToCalendar")}
              </AddToCalendarButton>
              {isDropped && (
                <CalMenu>
                  <CalMenuItem>
                    <SiGooglecalendar />
                    Google Calendar
                  </CalMenuItem>
                  <CalMenuItem>
                    <RiAppleLine />
                    Apple Calendar
                  </CalMenuItem>
                  <CalMenuItem>
                    <PiMicrosoftOutlookLogoThin />
                    Microsoft Calendar
                  </CalMenuItem>
                </CalMenu>
              )}
            </AddToCalendarMenu>
          </TimeAndDate>

          {"The Outdoor Church Picnic is a joyful opportunity for the congregation to gather together in a relaxed and family-friendly setting. Surrounded by nature, members can enjoy fellowship over food, laughter, and meaningful conversation while strengthening the bonds that make the church community feel like family. It’s a time to step away from busy schedules, share a meal on the grass, and celebrate the blessings of togetherness in God’s creation. Families and individuals of all ages can participate, whether enjoying a picnic blanket with friends, engaging in outdoor activities, or simply soaking in the joy of being part of a caring community. The Outdoor Church Picnic highlights the spirit of unity and hospitality, reminding everyone that the church is not just a building, but a living, thriving family in Christ."
            .split("\n")
            .map((subtext, index) => (
              <span
                style={{
                  display: "flex",
                  marginTop: "10px",
                }}
              >
                {subtext}
              </span>
            ))}
        </EventDescriptionText>
      </EventDescription>
    </DetailsContainer>
  );
}

const EventDetailsTopAttending = styled.div`
  font-size: 14px;
`;

const AddToCalendarMenu = styled.div`
  position: relative;
`;

const RSVPBtn = styled.div`
  pointer-events: ${(props) => (props.disabled ? "none" : "auto")};
  width: 130px;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  padding: 7px 15px;
  cursor: pointer;
  color: var(--lightThemeFontColor);
  border-radius: 6px;
  background-color: ${(props) => props.bg};
  color: white;
  font-size: 12px;
  svg {
    color: white;
    font-size: 23px;
  }

  &:hover {
    background-color: var(--themeColorDark);
    /* spin the svg icon only one time */

    svg {
      animation: ${(props) => props.spinIcon};
    }
  }
`;

const MyEventStatus = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  padding: 10px 10px;
  cursor: pointer;
  color: var(--lightThemeFontColor);
`;

const AddToCalendarButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #e7e7e7;
  font-size: 12px;
  height: 40px;
  width: fit-content;
  padding: 0px 10px;
  color: var(--lightThemeFontColor);
  border: 1px solid #ccc;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  gap: 10px;
`;

const CalMenu = styled.div`
  position: absolute;
  display: flex;
  flex-direction: column;
  margin-top: 5px;
  top: 100%;
  right: 0;
  background-color: #ffffff;
  border: 1px solid #ccc;
  border-radius: 5px;
  gap: 5px;
  padding: 10px;
  z-index: 1000;
`;

const CalMenuItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  padding: 5px;
  // do not break the text
  white-space: nowrap;
  font-size: 12px;
  gap: 10px;
  color: var(--lightThemeFontColor);
  cursor: pointer;
  &:hover {
    background-color: #f0f0f0;
  }
`;

const TimeAndDate = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  margin-bottom: 10px;
  background-color: aliceblue;
  color: #6e6e6e;
  /* border-radius: 10px; */
  padding: 15px;
  height: fit-content;
  width: 100%;
  /* box-shadow: 0px 0px 5px #ababab; */
`;

const EventDescriptionTitle = styled.div`
  font-size: 13px;
  font-weight: 500;
  color: #ccc;
  margin: 10px 0px 10px 10px;
`;

const DetailsContainer = styled.div`
  width: 100%;
  height: fit-content;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  border-radius: 10px;
  padding: 10px;
  background-color: white;
  box-shadow: 0 0 10px #ccc;
  @media (max-width: 768px) {
    height: fit-content;
  }
`;
const EventDetailsTop = styled.div`
  width: 100%;
  height: fit-content;
  padding: 10px;
  border-bottom: 1px solid #d6d6d6;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  color: var(--lightThemeFontColor);
`;

const EventDescription = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  height: fit-content;
  margin-top: 10px;
  width: 100%;
`;

const EventDescriptionText = styled.div`
  /* height: ${(props) => (props.hasMap ? "50%" : "100%")}; */
  height: fit-content;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  text-align: left;
  font-size: 15px;
  font-weight: 400;
  color: var(--lightThemeFontColor);
  margin-bottom: 40px;
  padding: 10px 10px 20px 10px;
  width: 100%;
`;
